/**
 * cellAddressParser.js
 * Excel-style cell address parsing and conversion utilities.
 * Supports A1, B2, AA100, ZZ9999 style addressing.
 */

import { columnLetterToIndex, columnIndexToLetter } from './columnLetterUtils';

/**
 * Parse a cell address string like "A1", "B2", "AA100"
 * Returns { col: 0, row: 0 } (zero-based indices)
 */
export function parseCellAddress(address) {
  if (!address || typeof address !== 'string') return null;

  const match = address.match(/^([A-Za-z]+)(\d+)$/);
  if (!match) return null;

  const colLetter = match[1].toUpperCase();
  const rowNumber = parseInt(match[2], 10);

  return {
    col: columnLetterToIndex(colLetter),
    row: rowNumber - 1, // Convert 1-based to 0-based
    colLetter,
    rowNumber,
    address: address.toUpperCase(),
  };
}

/**
 * Build a cell address string from zero-based col/row indices
 * e.g. (0, 0) → "A1", (25, 0) → "Z1", (26, 0) → "AA1"
 */
export function buildCellAddress(colIndex, rowIndex) {
  return `${columnIndexToLetter(colIndex)}${rowIndex + 1}`;
}

/**
 * Parse a range address like "A1:C10"
 * Returns { start: { col, row }, end: { col, row } }
 */
export function parseRangeAddress(rangeStr) {
  if (!rangeStr || typeof rangeStr !== 'string') return null;

  const parts = rangeStr.split(':');
  if (parts.length !== 2) return null;

  const start = parseCellAddress(parts[0].trim());
  const end   = parseCellAddress(parts[1].trim());

  if (!start || !end) return null;

  return {
    start,
    end,
    address: `${start.address}:${end.address}`,
    rowCount: end.row - start.row + 1,
    colCount: end.col - start.col + 1,
  };
}

/**
 * Build a range address from two cell coordinates
 */
export function buildRangeAddress(startCol, startRow, endCol, endRow) {
  return `${buildCellAddress(startCol, startRow)}:${buildCellAddress(endCol, endRow)}`;
}

/**
 * Convert AG Grid column field name to column index
 * given the ordered list of column fields from metadata.
 */
export function fieldToColIndex(field, columns) {
  return columns.findIndex((c) => c.field === field);
}

/**
 * Translate HyperFormula {row, col} to AG Grid {rowIndex, colId}
 */
export function hfToAgCoord(hfCoord, columns) {
  return {
    rowIndex: hfCoord.row,
    colId: columns[hfCoord.col]?.field ?? null,
  };
}

/**
 * Translate AG Grid {rowIndex, colId} to HyperFormula {row, col}
 */
export function agToHfCoord(rowIndex, colId, columns) {
  return {
    row: rowIndex,
    col: fieldToColIndex(colId, columns),
  };
}

/**
 * Validate whether a string is a valid cell address
 */
export function isValidCellAddress(address) {
  return /^[A-Za-z]+\d+$/.test(address);
}

/**
 * Validate whether a string is a valid range address
 */
export function isValidRangeAddress(address) {
  const parts = address.split(':');
  return parts.length === 2 && parts.every(isValidCellAddress);
}

/**
 * Get all cell addresses within a range
 */
export function getCellsInRange(rangeStr) {
  const range = parseRangeAddress(rangeStr);
  if (!range) return [];

  const cells = [];
  for (let r = range.start.row; r <= range.end.row; r++) {
    for (let c = range.start.col; c <= range.end.col; c++) {
      cells.push(buildCellAddress(c, r));
    }
  }
  return cells;
}
